import { memo, useEffect, useState } from 'react';
import { RotateCcw, Wand2 } from 'lucide-react';
import { FileUploader } from '@/components/forms/FileUploader';
import { GamePicker } from './GamePicker';
import { PosterPane } from './PosterPane';
import type { PosterPreset } from '@/lib/api/xurvey';
import { BackgroundStudioDialog } from './BackgroundStudioDialog';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import {
  CELEBRACIONES,
  FONDO_TAMANOS,
  FUENTES,
  LAYOUT_ALIGN,
  LAYOUT_FIT,
  NAV_LAYOUTS,
  TOKENS_COLOR,
  desnudo,
  type TokenColor,
} from './tokens';

type Pestana = 'apariencia' | 'estilo' | 'form' | 'cartel';

interface Props {
  design: Record<string, unknown>;
  tokens: Record<string, string>;
  /**
   * Lo que la encuesta trae de serie, para lo que este evento no ha tocado.
   *
   * Se enseña en gris y como marcador del campo. Sin ello, un color sin pisar
   * salía negro —el valor por defecto de `input[type=color]`— y el panel
   * contradecía al móvil de al lado sin que nadie pudiera saber cuál mentía.
   */
  heredado: { tokens: Record<string, string>; design: Record<string, unknown> };
  onDesign: (delta: Record<string, unknown>) => void;
  onToken: (key: TokenColor, value: string | null) => void;
  /** Idiomas que declara la encuesta y los que este evento ofrece. */
  /**
   * El juego de este despliegue y si el paso elegido lo lleva.
   *
   * Se ofrece **sólo en el paso que tiene juego**: un selector colgando de una
   * encuesta que no tiene ninguno es una promesa falsa, y buscarlo en un panel
   * general obliga a recordar si esta encuesta jugaba o no.
   */
  gameCode: string;
  onGameCode: (code: string) => void;
  /** El botón bajo el QR del premio. Vacío = el de la encuesta. */
  prizeLinkUrl: string;
  prizeLinkLabel: string;
  prizeLinkStyle: '' | 'link' | 'button' | 'iconButton';
  onPrizeLink: (delta: { url?: string; label?: string; style?: 'link' | 'button' | 'iconButton' | '' }) => void;
  /**
   * Si la ENCUESTA lleva juego — no si el paso seleccionado lo lleva.
   *
   * Antes la sección sólo salía con el paso del juego seleccionado en la lista
   * de la izquierda, y como nada lo decía, el enlace del premio «nunca se
   * veía». Si la encuesta juega, el juego y su enlace se ofrecen siempre.
   */
  encuestaConJuego: boolean;
  /** Si la encuesta tiene pantalla de bienvenida, para ofrecer su imagen. */
  encuestaConWelcome: boolean;
  /** La imagen de la bienvenida de ESTE evento. Vacío = la de la encuesta. */
  welcome: { imageUrl?: string; imageRole?: 'hero' | 'logo' | 'background' };
  onWelcome: (delta: { imageUrl?: string; imageRole?: 'hero' | 'logo' | 'background' }) => void;
  /**
   * El cartel de ESTE despliegue y su slug, para poder verlo por URL.
   *
   * Se personaliza aquí igual que en Xurvey —lo pidió quien lo usa— pero lo
   * que se toca son los overrides del despliegue: la plantilla del cartel
   * sigue viviendo en la encuesta.
   */
  slug: string;
  poster: Record<string, unknown>;
  onPoster: (delta: Record<string, unknown>) => void;
  /** El diseño de cartel guardado del que partió este despliegue, y si se tocó después. */
  posterPresetId: number | null;
  posterPresetTocado: boolean;
  onAplicarPosterPreset: (preset: PosterPreset) => void;
}

/**
 * Las herramientas de diseño de un despliegue.
 *
 * Es el mismo panel que el editor de Xurvey y a propósito: quien aprende a
 * pintar una encuesta allí no tiene que aprenderlo otra vez aquí, y quien
 * aprenda aquí lo sabrá hacer en Xamplin y en DiD·ox. **Éste es el estándar**;
 * las otras apps lo mutan, no lo reinventan.
 *
 * Lo que NO tiene, y no por falta de tiempo: nada de estructura. No se añaden
 * pasos ni componentes, no se editan etiquetas y no se mueve nada de sitio.
 * La app decide cómo se ve y cuándo se usa; la encuesta decide qué se pregunta.
 *
 * Todo escribe en los `overrides` del despliegue y nada toca la encuesta: el
 * mismo diseño puede estar vivo en treinta eventos y cada uno se pinta a su
 * manera sin pisarle el trabajo a los demás.
 */
function DesignPaneBase({
  design,
  tokens,
  heredado,
  onDesign,
  onToken,
  gameCode,
  onGameCode,
  prizeLinkUrl,
  prizeLinkLabel,
  prizeLinkStyle,
  onPrizeLink,
  encuestaConJuego,
  encuestaConWelcome,
  welcome,
  onWelcome,
  slug,
  poster,
  onPoster,
  posterPresetId,
  posterPresetTocado,
  onAplicarPosterPreset,
}: Props) {
  const t = useT();
  const [tab, setTab] = useState<Pestana>('apariencia');
  const [estudioAbierto, setEstudioAbierto] = useState(false);

  return (
    <div className="flex h-full flex-col">
      {/* Los estilos guardados ya no van aquí: están bajo el selector de
          encuesta, que es de donde se parte. Ver DesignJourneySection. */}
      <div data-tour="dj-tabs" className="flex flex-none border-b border-[var(--color-border-default)]">
        {(['apariencia', 'estilo', 'form', 'cartel'] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setTab(p)}
            className={cn(
              'h-9 flex-1 border-b-2 text-[11px] uppercase tracking-[0.08em]',
              tab === p
                ? 'border-[var(--color-accent-ink)] text-[var(--color-fg-primary)]'
                : 'border-transparent text-[var(--color-fg-muted)]',
            )}
          >
            {t(`designJourney.tab.${p}`)}
          </button>
        ))}
      </div>

      <div className="flex flex-1 flex-col gap-3 overflow-auto p-3">
        {tab === 'apariencia' ? (
          <>
            <Seccion titulo={t('designJourney.logo')} />
            <FileUploader
              value={(design.logoUrl as string) ?? null}
              onChange={(url) => onDesign({ logoUrl: url ?? '', logoOn: !!url })}
              preset="logo"
            />
            <Slider
              etiqueta={t('designJourney.opacity')}
              valor={num(design.logoOpacity, num(heredado.design.logoOpacity, 1))}
              onChange={(v) => onDesign({ logoOpacity: v })}
            />

            <Seccion titulo={t('designJourney.background')} />
            <FileUploader
              value={desnudo((design.backgroundImage as string) ?? null)}
              // Se guarda envuelto en `url(...)` porque es lo que espera el
              // renderer: un valor desnudo se toma por un degradado o un color.
              onChange={(url) => onDesign({ backgroundImage: url ? `url("${url}")` : '' })}
            />
            {/*
              Subir una imagen y diseñarla son dos caminos al mismo sitio, y por
              eso están juntos: uno para quien ya tiene el arte de la marca, otro
              para quien no tiene nada y necesita algo digno en un minuto.
              Ambos acaban escribiendo el mismo `backgroundImage`.
            */}
            <button
              type="button"
              onClick={() => setEstudioAbierto(true)}
              className="flex items-center justify-center gap-1.5 rounded-lg border border-[var(--color-xk-border)] py-2 text-[11px] font-bold text-[var(--color-xk-lavender)] transition-colors hover:border-[var(--color-accent-ink)] hover:text-[var(--color-accent-ink)]"
            >
              <Wand2 className="h-3.5 w-3.5" />
              {t('designJourney.designBackground')}
            </button>
            <Select
              etiqueta={t('designJourney.backgroundSize')}
              valor={(design.backgroundSize as string) ?? ''}
              opciones={FONDO_TAMANOS}
              onChange={(v) => onDesign({ backgroundSize: v })}
              vacio={t('designJourney.fromSurvey')}
            />
            <Slider
              etiqueta={t('designJourney.opacity')}
              valor={num(design.backgroundOpacity, num(heredado.design.backgroundOpacity, 1))}
              onChange={(v) => onDesign({ backgroundOpacity: v })}
            />

            {/*
              La imagen de la BIENVENIDA de este evento — el patrocinador de
              este festival— sin tocar la de la encuesta. Es apariencia, no
              pregunta: el runtime la funde en el componente al servir, igual
              que el logo y el fondo se funden en el diseño. Sólo si la
              encuesta tiene bienvenida: ofrecerla sin pantalla donde ponerla
              sería una promesa falsa.
            */}
            {encuestaConWelcome ? (
              <>
                <Seccion titulo={t('designJourney.welcome')} />
                <FileUploader
                  value={welcome.imageUrl || null}
                  onChange={(url) => onWelcome({ imageUrl: url ?? '' })}
                  preset="logo"
                />
                <Select
                  etiqueta={t('designJourney.welcomeRole')}
                  valor={welcome.imageRole ?? ''}
                  opciones={['hero', 'logo', 'background']}
                  onChange={(v) => onWelcome({ imageRole: (v || 'hero') as 'hero' | 'logo' | 'background' })}
                  vacio={t('designJourney.fromSurvey')}
                  traducir="designJourney.welcomeRole."
                />
                <p className="text-[10px] leading-snug text-[var(--color-fg-muted)]">
                  {t('designJourney.welcomeHint')}
                </p>
              </>
            ) : null}

            {encuestaConJuego ? (
              <>
                <Seccion titulo={t('designJourney.game')} />
                <GamePicker
                  valor={gameCode}
                  onChange={onGameCode}
                  primary={tokens.primary || heredado.tokens.primary || '#7c3aed'}
                  accent={tokens.accent || heredado.tokens.accent || '#22d3ee'}
                />

                {/*
                  El logo de MARCA DE AGUA de los juegos (sept 2026). Antes
                  los lienzos usaban el logo de cabecera —o su filigrana de
                  relleno «brand»—; esto deja subir uno pensado para el fondo
                  tenue (mosaico del scratch, valla del fútbol, superficie del
                  pirinola, bola del xinkit). Vacío = cae al logo de arriba.
                */}
                <Seccion titulo={t('designJourney.watermark')} />
                <FileUploader
                  value={(design.watermarkUrl as string) ?? null}
                  onChange={(url) => onDesign({ watermarkUrl: url ?? '' })}
                  preset="logo"
                />
                <p className="text-[11px] leading-snug text-[var(--color-fg-muted)]">
                  {t('designJourney.watermarkHint')}
                </p>

                {/*
                  A dónde lleva el botón que sale bajo el QR del premio.
                  
                  Va aquí, pegado al juego, porque es lo siguiente que ve quien
                  acaba de jugar. Vive en la ENCUESTA —y la plantilla base trae
                  `xotski.com`, bloqueada— así que sin esto ninguna activación
                  podía mandar a su propia landing sin duplicar la encuesta.
                  Vacío quita el botón; sin tocar, manda el de la encuesta.
                */}
                <Seccion titulo={t('designJourney.prizeLink')} />
                <Texto
                  etiqueta={t('designJourney.prizeLinkUrl')}
                  valor={prizeLinkUrl}
                  marcador={t('designJourney.prizeLinkPlaceholder')}
                  onChange={(v) => onPrizeLink({ url: v })}
                />
                <Texto
                  etiqueta={t('designJourney.prizeLinkLabel')}
                  valor={prizeLinkLabel}
                  marcador={t('designJourney.prizeLinkLabelPlaceholder')}
                  onChange={(v) => onPrizeLink({ label: v })}
                />
                {/* Cómo se pinta: enlace de texto, botón relleno o botón con icono. */}
                <div className="flex flex-col gap-1">
                  <span className="text-[11px] text-[var(--color-fg-muted)]">{t('designJourney.prizeLinkStyle')}</span>
                  <div role="radiogroup" aria-label={t('designJourney.prizeLinkStyle')} className="flex flex-wrap gap-1.5">
                    {(['link', 'button', 'iconButton'] as const).map((st) => {
                      const marcado = (prizeLinkStyle || 'button') === st;
                      return (
                        <button
                          key={st}
                          type="button"
                          role="radio"
                          aria-checked={marcado}
                          onClick={() => onPrizeLink({ style: st })}
                          className={cn(
                            'rounded-md px-2 py-1 text-[11px] font-semibold transition-colors',
                            marcado
                              ? 'bg-[var(--color-accent)] text-[var(--color-fg-on-accent)]'
                              : 'bg-[var(--color-bg-input)] text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]',
                          )}
                        >
                          {t(`designJourney.prizeLinkStyle.${st}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <p className="text-[10px] leading-snug text-[var(--color-fg-muted)]">
                  {t('designJourney.prizeLinkHint')}
                </p>
              </>
            ) : null}

            <Seccion titulo={t('designJourney.celebration')} />
            <Select
              etiqueta={t('designJourney.celebration')}
              valor={(design.celebration as string) ?? ''}
              opciones={CELEBRACIONES}
              onChange={(v) => onDesign({ celebration: v || undefined })}
              vacio={t('designJourney.fromSurvey')}
              traducir="designJourney.celebration."
            />

          </>
        ) : null}

        {tab === 'estilo' ? (
          <>
            <Seccion titulo={t('designJourney.palette')} />
            {TOKENS_COLOR.map(({ key, label }) => (
              <FilaColor
                key={key}
                etiqueta={label}
                valor={tokens[key] ?? ''}
                heredado={heredado.tokens[key] ?? ''}
                onChange={(v) => onToken(key, v)}
                onQuitar={() => onToken(key, null)}
                textoQuitar={t('designJourney.backToTheme')}
              />
            ))}

            <Seccion titulo={t('designJourney.typography')} />
            <Select
              etiqueta={t('designJourney.fontDisplay')}
              valor={(design.fontDisplay as string) ?? ''}
              opciones={FUENTES}
              onChange={(v) => onDesign({ fontDisplay: v || null })}
              vacio={t('designJourney.fromSurvey')}
            />
            <Select
              etiqueta={t('designJourney.fontBody')}
              valor={(design.fontBody as string) ?? ''}
              opciones={FUENTES}
              onChange={(v) => onDesign({ fontBody: v || null })}
              vacio={t('designJourney.fromSurvey')}
            />
            <Slider
              etiqueta={t('designJourney.textOpacity')}
              valor={num(design.textOpacity, num(heredado.design.textOpacity, 1))}
              min={0.4}
              onChange={(v) => onDesign({ textOpacity: v })}
            />
            <p className="text-[10px] leading-snug text-[var(--color-fg-muted)]">
              {t('designJourney.textOpacityHint')}
            </p>
          </>
        ) : null}

        {tab === 'form' ? (
          <>
            <Seccion titulo={t('designJourney.container')} />
            <FilaColor
              etiqueta={t('designJourney.cardColor')}
              valor={(design.cardColor as string) ?? ''}
              heredado={(heredado.design.cardColor as string) ?? ''}
              onChange={(v) => onDesign({ cardColor: v })}
              onQuitar={() => onDesign({ cardColor: 'transparent' })}
              textoQuitar={t('designJourney.transparent')}
            />
            <Slider
              etiqueta={t('designJourney.opacity')}
              valor={num(design.cardOpacity, num(heredado.design.cardOpacity, 1))}
              onChange={(v) => onDesign({ cardOpacity: v })}
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!design.borderOn}
                onChange={(e) => onDesign({ borderOn: e.target.checked })}
              />
              {t('designJourney.border')}
            </label>
            {design.borderOn ? (
              <FilaColor
                etiqueta={t('designJourney.borderColor')}
                valor={(design.borderColor as string) ?? ''}
                heredado={(heredado.design.borderColor as string) ?? ''}
                onChange={(v) => onDesign({ borderColor: v })}
                onQuitar={() => onDesign({ borderColor: '' })}
                textoQuitar={t('designJourney.backToTheme')}
              />
            ) : null}

            {/*
              Las dos esquinas de arriba del formulario, en píxeles.

              Arranca en lo que ya se está viendo —lo del despliegue, si no lo
              de la encuesta, si no el radio del tema— para que el mando ajuste
              en vez de saltar a cero en cuanto se toca.
            */}
            <div className="flex items-center gap-1.5">
              <Slider
                etiqueta={t('designJourney.radiusTop')}
                valor={num(
                  design.cardRadiusTop,
                  num(heredado.design.cardRadiusTop, pxDe(heredado.tokens.radius)),
                )}
                max={100}
                step={1}
                unidad="px"
                onChange={(v) => onDesign({ cardRadiusTop: v })}
              />
              <button
                type="button"
                onClick={() => onDesign({ cardRadiusTop: null })}
                disabled={design.cardRadiusTop == null}
                title={t('designJourney.backToTheme')}
                className="w-4 text-[11px] text-[var(--color-fg-muted)] hover:text-[var(--color-fg)] disabled:opacity-25"
              >
                ↺
              </button>
            </div>

            <Seccion titulo={t('designJourney.stepFrame')} />
            <Select
              etiqueta={t('designJourney.layoutFit')}
              valor={(design.layoutFit as string) ?? ''}
              opciones={LAYOUT_FIT}
              onChange={(v) => onDesign({ layoutFit: v })}
              vacio={t('designJourney.fromSurvey')}
            />
            <Select
              etiqueta={t('designJourney.layoutAlign')}
              valor={(design.layoutAlign as string) ?? ''}
              opciones={LAYOUT_ALIGN}
              onChange={(v) => onDesign({ layoutAlign: v })}
              vacio={t('designJourney.fromSurvey')}
            />

            <Seccion titulo={t('designJourney.navigation')} />
            <Select
              etiqueta={t('designJourney.navLayout')}
              valor={(design.navLayout as string) ?? ''}
              opciones={NAV_LAYOUTS}
              onChange={(v) => onDesign({ navLayout: v })}
              vacio={t('designJourney.fromSurvey')}
            />
          </>
        ) : null}

        {tab === 'cartel' ? (
          <PosterPane
            slug={slug}
            poster={poster}
            onPoster={onPoster}
            posterPresetId={posterPresetId}
            posterPresetTocado={posterPresetTocado}
            onAplicarPreset={onAplicarPosterPreset}
          />
        ) : null}
      </div>

      {estudioAbierto ? (
        <BackgroundStudioDialog
          open
          onOpenChange={setEstudioAbierto}
          onUse={(url, paleta) => {
            onDesign({ backgroundImage: `url("${url}")` });
            /*
              Los colores, sólo si se pidieron, y sólo los que este panel sabe
              pisar. El generador propone un tema entero; aquí se escriben los
              tokens del despliegue uno a uno porque `onToken` es la única vía
              que respeta el «vacío = lo que traiga la encuesta».
            */
            if (!paleta) return;
            if (paleta.bg) onToken('bg', paleta.bg);
            if (paleta.surface) onToken('surface', paleta.surface);
            if (paleta.primary) onToken('primary', paleta.primary);
            if (paleta.accent) onToken('accent', paleta.accent);
            if (paleta.text) onToken('text', paleta.text);
          }}
        />
      ) : null}
    </div>
  );
}

// ── Piezas ────────────────────────────────────────────────────────────

/** Campo de texto del panel, con la misma pinta que su `Select` vecino. */
function Texto({
  etiqueta,
  valor,
  marcador,
  onChange,
}: {
  etiqueta: string;
  valor: string;
  marcador: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] text-[var(--color-fg-muted)]">{etiqueta}</span>
      <input
        type="text"
        value={valor}
        placeholder={marcador}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-input)] px-2 text-[12px] text-[var(--color-fg-primary)] placeholder:text-[var(--color-fg-muted)] focus:border-[var(--color-accent-ink)] focus:outline-none"
      />
    </label>
  );
}

const Seccion = ({ titulo }: { titulo: string }) => (
  <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-[var(--color-fg-muted)]">
    {titulo}
  </p>
);

function FilaColor({
  etiqueta,
  valor,
  heredado,
  onChange,
  onQuitar,
  textoQuitar,
}: {
  etiqueta: string;
  valor: string;
  /** El de la encuesta, cuando este evento no lo ha tocado. */
  heredado: string;
  onChange: (v: string) => void;
  onQuitar: () => void;
  textoQuitar: string;
}) {
  const propio = !!valor;
  /*
   * Eco local del campo.
   *
   * El borrador se actualiza en transición para que arrastrar el selector no
   * atasque el repintado del panel; si el `input` leyera sólo de ahí, se vería
   * ir por detrás del dedo. Con el eco, el campo responde al instante y el
   * borrador llega cuando puede.
   */
  const [eco, setEco] = useState(valor);
  useEffect(() => setEco(valor), [valor]);

  // Lo que se ve de verdad: lo propio si lo hay, y si no lo heredado. Es lo que
  // tiene que enseñar la muestra, o el panel contradice al móvil.
  const efectivo = eco || heredado;
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={/^#[0-9a-fA-F]{6}$/.test(efectivo) ? efectivo : '#000000'}
        onChange={(e) => {
          setEco(e.target.value);
          onChange(e.target.value);
        }}
        className="h-6 w-6 flex-none cursor-pointer rounded border border-[var(--color-border-default)] bg-transparent p-0"
        aria-label={etiqueta}
      />
      <span className="flex-1 truncate text-[12px]">{etiqueta}</span>
      <input
        value={eco}
        onChange={(e) => {
          setEco(e.target.value);
          onChange(e.target.value);
        }}
        // El heredado como marcador: en gris se lee «esto es lo que hay y no lo
        // has tocado», que es distinto de un campo vacío.
        placeholder={heredado || '—'}
        className={cn(
          'w-[76px] rounded border border-[var(--color-border-default)] bg-transparent px-1 text-right font-mono text-[10px]',
          !propio && 'text-[var(--color-fg-muted)]',
        )}
      />
      {/* Volver al del tema es un gesto explícito: borrar el texto a mano deja
          un valor vacío, que no es lo mismo que «el que traiga la encuesta». */}
      <button
        type="button"
        onClick={onQuitar}
        disabled={!propio}
        title={textoQuitar}
        className="text-[var(--color-fg-subtle)] disabled:opacity-30"
      >
        <RotateCcw className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function Slider({
  etiqueta,
  valor,
  onChange,
  min = 0,
  max = 1,
  step = 0.05,
  // Los mandos de este panel eran todos opacidades, o sea 0–1 leídos en tanto
  // por ciento. Con `px` el mando trabaja en píxeles y lo dice al lado, para
  // que un 40 no se lea como un 40 %.
  unidad,
}: {
  etiqueta: string;
  valor: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unidad?: 'px';
}) {
  return (
    <label className="flex items-center gap-2 text-[12px]">
      <span className="flex-1 truncate">{etiqueta}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={valor}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-[110px]"
      />
      <span className="w-9 text-right font-mono text-[10px] text-[var(--color-fg-muted)]">
        {unidad === 'px' ? `${Math.round(valor)}px` : Math.round(valor * 100)}
      </span>
    </label>
  );
}

function Select({
  etiqueta,
  valor,
  opciones,
  onChange,
  vacio,
  traducir,
}: {
  etiqueta: string;
  valor: string;
  opciones: readonly string[];
  onChange: (v: string) => void;
  vacio: string;
  traducir?: string;
}) {
  const t = useT();
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-[var(--color-fg-muted)]">
        {etiqueta}
      </span>
      <select
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-input)] px-2 text-[12px] text-[var(--color-fg-primary)]"
      >
        <option value="">{vacio}</option>
        {opciones.map((o) => (
          <option key={o} value={o}>
            {traducir ? t(`${traducir}${o}`) : o}
          </option>
        ))}
      </select>
    </label>
  );
}

const num = (v: unknown, porDefecto: number) => (typeof v === 'number' ? v : porDefecto);

/** `'18px'` → `18`. Los tokens de forma llevan unidad y el mando pide número. */
const pxDe = (v: string | undefined) => {
  const n = Number.parseFloat(v ?? '');
  return Number.isFinite(n) ? Math.min(100, Math.max(0, Math.round(n))) : 0;
};

/** `url("…")` → `…`. El uploader trabaja con la ruta, no con el CSS. */
/**
 * Memorizado a propósito. Se repinta cuando cambia lo que edita, no cuando cambia cualquier cosa de la pantalla.
 * Sus manejadores llegan estables desde `DesignJourneySection`, que es lo que
 * hace que esto sirva de algo.
 */
export const DesignPane = memo(DesignPaneBase);
